import { Injectable } from '@angular/core';
import { Store } from '../../../services/store';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { DocumentsProvider } from './documents.provider';
import { BomAttachmentListItemDto } from './models/bom-attachment-list-item.dto';
import { BomAttachmentListDto } from './models/bom-attachment-list.dto';
import { toVoid } from '../../../utils/void';

// Nothing changes it, so it is not state (0091)
export const PAGE_SIZE = 50;

// By file format, what a person looking for "the drawing" or "the sheet" says (0125)
export type AttachmentKind = 'image' | 'pdf' | 'spreadsheet' | 'text';

export type DocumentsCriteria = Pick<DocumentsState, 'search' | 'typeFilter' | 'page'>;

export interface DocumentsState {
  items: BomAttachmentListItemDto[] | undefined;
  // the size of the whole narrowed result, not of the page
  totalCount: number;
  page: number;
  loading: boolean;
  search: string;
  typeFilter: AttachmentKind | undefined;
  // True when the tenant stores no attachments at all — as opposed to none
  // matching the current search/filter. Drives the "add files to BOM
  // elements" invitation, which must win over the no-results messages
  catalogEmpty: boolean;
}

// Unlike /bom, the list opens on Wszystkie: every file is documentation
const INITIAL_STATE: DocumentsState = {
  items: undefined,
  totalCount: 0,
  page: 1,
  loading: false,
  search: '',
  typeFilter: undefined,
  catalogEmpty: false,
};

@Injectable({ providedIn: 'root' })
export class DocumentsStore extends Store<DocumentsState> {
  constructor(private provider: DocumentsProvider) {
    super(INITIAL_STATE);
  }

  // The URL owns the criteria (0092): the page hands over all three at once
  // and the store mirrors them. Only the first load shows Loading…, later
  // ones keep the rows until the answer
  load(criteria: DocumentsCriteria): Observable<void> {
    this.update(state => ({ ...state, ...criteria, loading: state.items === undefined }));

    return this.fetch();
  }

  contentUrl(attachmentId: number): string {
    return this.provider.contentUrl(attachmentId);
  }

  // Search, filter and page always travel together, so state and request
  // cannot drift. The server orders newest first; its order is the order
  private fetch(): Observable<void> {
    const { search, typeFilter, page } = this.state;

    return this.provider.getAttachmentList(search || undefined, typeFilter, page, PAGE_SIZE)
      .pipe(
        switchMap(list => this.withCatalogEmpty(list, !!search || !!typeFilter)),
        tap(({ list, catalogEmpty }) => this.update(state => ({
          ...state,
          items: list.items,
          totalCount: list.totalCount,
          catalogEmpty,
          loading: false
        }))),
        toVoid()
      );
  }

  // A narrowed query that comes back empty cannot tell "no PDFs" from "no
  // files at all", so probe once without criteria — only in that case, an
  // unnarrowed result already answers the question with its count
  private withCatalogEmpty(list: BomAttachmentListDto, narrowed: boolean): Observable<{ list: BomAttachmentListDto, catalogEmpty: boolean }> {
    if (list.totalCount > 0 || !narrowed) {
      return of({ list, catalogEmpty: list.totalCount === 0 });
    }

    return this.provider.getAttachmentList(undefined, undefined, undefined, 1)
      .pipe(map(all => ({ list, catalogEmpty: all.totalCount === 0 })));
  }
}
